import angular from 'angular';
import { NsjRoutingType } from '@core/nsj-core';
import { IBureauTenantResumo } from './models/bureaus.model';
import {
    IBureauEquipeAdministrador,
    IBureauEquipeMembro,
    IBureauEquipeOffset,
} from './models/bureaus-equipes.model';

export class MeurhBureausEquipesService {
    static $inject = ['$http', 'nsjRouting', '$q'];

    public administradoresBusy = false;
    public membrosBusy = false;
    public administradoresDeferred: angular.IDeferred<unknown> | null = null;
    public membrosDeferred: angular.IDeferred<unknown> | null = null;

    constructor(
        public $http: angular.IHttpService,
        public nsjRouting: NsjRoutingType,
        public $q: angular.IQService,
    ) { }

    public getMeusTenants(): angular.IPromise<IBureauTenantResumo[]> {
        return this.$q<IBureauTenantResumo[]>((resolve, reject) => {
            this.$http
                .get<IBureauTenantResumo[]>(
                    this.nsjRouting.generate('bureaus_equipes_index_meus_tenants', {}, true)
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /** Sem `offset`, a primeira página; com ele, a página seguinte à pessoa informada. */
    public getAdministradores(
        filtro: string = '',
        offset: IBureauEquipeOffset | null = null
    ): angular.IPromise<IBureauEquipeAdministrador[]> {
        if (this.administradoresDeferred) {
            this.administradoresDeferred.resolve();
        }

        const deferred = this.$q.defer();
        this.administradoresDeferred = deferred;
        this.administradoresBusy = true;

        return this.$q<IBureauEquipeAdministrador[]>((resolve, reject) => {
            this.$http
                .get<IBureauEquipeAdministrador[]>(
                    this.nsjRouting.generate('bureaus_equipes_index_administradores', this.paramsLista(filtro, offset), true),
                    { timeout: deferred.promise }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    // a requisição cancelada por uma nova não desliga o busy da nova
                    if (this.administradoresDeferred === deferred) {
                        this.administradoresBusy = false;
                    }
                });
        });
    }

    /** `tenantId` chega já convertido: o backend valida com `is_int()` e recusa string. */
    public createAdministrador(email: string, tenantId: number): angular.IPromise<{ bureauusuario: string, bureauequipeadministrador: string }> {
        return this.$q<{ bureauusuario: string, bureauequipeadministrador: string }>((resolve, reject) => {
            this.$http
                .post<{ bureauusuario: string, bureauequipeadministrador: string }>(
                    this.nsjRouting.generate('bureaus_equipes_create_administrador', {}, true),
                    { email, tenant_id: tenantId }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public createAdministradorTenant(bureauusuario: string, tenantId: number): angular.IPromise<{ bureauequipeadministrador: string }> {
        return this.$q<{ bureauequipeadministrador: string }>((resolve, reject) => {
            this.$http
                .post<{ bureauequipeadministrador: string }>(
                    this.nsjRouting.generate('bureaus_equipes_create_administrador_tenant', { bureauusuario }, true),
                    { tenant_id: tenantId }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteAdministradorTenant(bureauusuario: string, bureauequipeadministrador: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_equipes_delete_administrador_tenant', { bureauusuario, bureauequipeadministrador }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteAdministrador(bureauusuario: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_equipes_delete_administrador', { bureauusuario }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /** Sem `offset`, a primeira página; com ele, a página seguinte à pessoa informada. */
    public getMembros(
        filtro: string = '',
        offset: IBureauEquipeOffset | null = null
    ): angular.IPromise<IBureauEquipeMembro[]> {
        if (this.membrosDeferred) {
            this.membrosDeferred.resolve();
        }

        const deferred = this.$q.defer();
        this.membrosDeferred = deferred;
        this.membrosBusy = true;

        return this.$q<IBureauEquipeMembro[]>((resolve, reject) => {
            this.$http
                .get<IBureauEquipeMembro[]>(
                    this.nsjRouting.generate('bureaus_equipes_index_membros', this.paramsLista(filtro, offset), true),
                    { timeout: deferred.promise }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    // a requisição cancelada por uma nova não desliga o busy da nova
                    if (this.membrosDeferred === deferred) {
                        this.membrosBusy = false;
                    }
                });
        });
    }

    /** `tenantIds` chega já convertido: o backend valida cada item com `is_int()`. */
    public createMembro(
        email: string,
        tenantIds: number[]
    ): angular.IPromise<{ bureauusuario: string, vinculos: { bureauequipemembro: string, tenant_id: number }[] }> {
        return this.$q<{ bureauusuario: string, vinculos: { bureauequipemembro: string, tenant_id: number }[] }>((resolve, reject) => {
            this.$http
                .post<{ bureauusuario: string, vinculos: { bureauequipemembro: string, tenant_id: number }[] }>(
                    this.nsjRouting.generate('bureaus_equipes_create_membro', {}, true),
                    { email, tenant_ids: tenantIds }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public createMembroTenant(bureauusuario: string, tenantId: number): angular.IPromise<{ bureauequipemembro: string }> {
        return this.$q<{ bureauequipemembro: string }>((resolve, reject) => {
            this.$http
                .post<{ bureauequipemembro: string }>(
                    this.nsjRouting.generate('bureaus_equipes_create_membro_tenant', { bureauusuario }, true),
                    { tenant_id: tenantId }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteMembroTenant(bureauusuario: string, bureauequipemembro: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_equipes_delete_membro_tenant', { bureauusuario, bureauequipemembro }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteMembro(bureauusuario: string): angular.IPromise<void> {
        return this.$q<void>((resolve, reject) => {
            this.$http
                .delete<void>(
                    this.nsjRouting.generate('bureaus_equipes_delete_membro', { bureauusuario }, true)
                )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    private paramsLista(filtro: string, offset: IBureauEquipeOffset | null): { filter?: string, offset?: IBureauEquipeOffset } {
        const params: { filter?: string, offset?: IBureauEquipeOffset } = {};

        if (filtro) {
            params.filter = filtro;
        }

        if (offset) {
            params.offset = offset;
        }

        return params;
    }
}
